/**
 * Pure view-model for the user-detail compliance area: merges the in-app
 * acceptances and the offline ones into one list tagged by source.
 *
 * The admin detail payload carries only a SUMMARY of offline acceptances;
 * `reference`, `note`, the actors and the revocation reason exist only on
 * the permissioned list endpoint, so they reach a row exclusively through
 * `details` (null when the viewer cannot read the full record).
 */
import type { TosAcceptanceSummary } from "../types";
import type { OfflineAcceptance, OfflineAcceptanceSummary } from "./types";

export interface InAppAcceptanceRow {
  readonly kind: "in-app";
  readonly id: string;
  readonly title: string;
  readonly at: string;
}

export interface OfflineAcceptanceRow {
  readonly kind: "offline";
  readonly id: string;
  readonly documentTypeId: string;
  readonly title: string;
  readonly at: string;
  readonly revokedAt: string | null;
  readonly details: OfflineAcceptance | null;
}

export type AcceptanceRow = InAppAcceptanceRow | OfflineAcceptanceRow;

interface BuildInput {
  readonly inApp: readonly TosAcceptanceSummary[];
  readonly offline: readonly OfflineAcceptanceSummary[];
  readonly details: readonly OfflineAcceptance[];
}

function toOfflineRow(
  summary: OfflineAcceptanceSummary,
  detailsById: ReadonlyMap<string, OfflineAcceptance>,
): OfflineAcceptanceRow {
  const details = detailsById.get(summary.id) ?? null;
  return {
    kind: "offline",
    id: summary.id,
    documentTypeId: summary.documentTypeId,
    title: details?.documentTypeName ?? summary.documentTypeKey,
    at: summary.recordedAt,
    revokedAt: summary.revokedAt,
    details,
  };
}

export function buildAcceptanceRows({
  inApp,
  offline,
  details,
}: BuildInput): readonly AcceptanceRow[] {
  const detailsById = new Map(details.map((d) => [d.id, d] as const));
  const inAppRows = inApp.map((a): InAppAcceptanceRow => ({
    kind: "in-app",
    id: a.tosId,
    title: a.tosVersion,
    at: a.acceptedAt,
  }));
  const offlineRows = offline.map((o) => toOfflineRow(o, detailsById));
  return [...inAppRows, ...offlineRows].sort(byNewestFirst);
}

/** Epoch millis; an unparseable date counts as the oldest possible. */
const timeOf = (iso: string): number => {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
};

/** Newest first; ties (and bad dates) fall back to the id so the order is stable. */
function byNewestFirst(a: AcceptanceRow, b: AcceptanceRow): number {
  const ta = timeOf(a.at);
  const tb = timeOf(b.at);
  if (ta === tb) return a.id.localeCompare(b.id);
  return ta > tb ? -1 : 1;
}

/** User ids of everyone who recorded / revoked an offline row that has its full record. */
export function actorIdsOf(rows: readonly AcceptanceRow[]): readonly string[] {
  return rows.flatMap((r) => {
    if (r.kind !== "offline" || !r.details) return [];
    return r.details.revokedByUserId
      ? [r.details.recordedByUserId, r.details.revokedByUserId]
      : [r.details.recordedByUserId];
  });
}

/** Document types that already have a live offline acceptance (API allows one per type). */
export function liveDocumentTypeIds(
  offline: readonly OfflineAcceptanceSummary[],
): ReadonlySet<string> {
  return new Set(offline.filter((o) => o.revokedAt === null).map((o) => o.documentTypeId));
}
